import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { Building, Search, Eye, LayoutDashboard } from 'lucide-react';
import { Database } from '@/integrations/supabase/types';
import { MemberSelector } from '@/components/support/MemberSelector';
import { SupportAccessReasonDialog } from '@/components/support/SupportAccessReasonDialog';
import { TopBar } from '@/components/layout/TopBar';
import { SupportShopAccessRequests } from '@/components/support/SupportShopAccessRequests';

type Shop = Database['public']['Tables']['shops']['Row'];

export default function SupportShops() {
  const navigate = useNavigate();
  const location = useLocation();
  const { startSupportSession, isInSupportMode, canAccessSupportMode } = useSupportMode();
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    // IV-D-23 R1 FIX-3: gate data fetch + all UI chrome behind
    // canAccessSupportMode (MANAGER of an internal shop). The route
    // above (/support/shops) only checks generic MANAGER/OWNER role,
    // so a customer-shop manager could previously land here and see a
    // partially-empty support UI. RLS protected the data either way,
    // but the UI chrome (Support Mode headline, Open-in-Support-Mode
    // buttons) shouldn't render for non-internal operators.
    if (canAccessSupportMode) {
      loadShops();
    } else {
      setLoading(false);
    }
  }, [canAccessSupportMode]);

  const loadShops = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('shops')
      .select('*')
      .order('name');

    if (!error && data) {
      setShops(data);
    }
    setLoading(false);
  };

  // BJ-N5/BJ-N6: the operator must state a disclosure-safe reason BEFORE the
  // shop is opened, so the dialog gates the session rather than following it.
  // 'Accessed from support shops list' is deliberately NOT reused as the
  // reason: it records where the operator clicked, not why privileged access
  // was required. It stays as the private internal note.
  const [pendingShop, setPendingShop] = useState<{ id: string; name: string } | null>(null);

  const handleOpenShop = (shopId: string) => {
    const shop = shops.find((s) => s.id === shopId);
    setPendingShop({ id: shopId, name: shop?.name ?? 'this shop' });
  };

  const handleConfirmAccess = async (disclosureReason: string) => {
    if (!pendingShop) return;
    const currentPath = location.pathname;
    await startSupportSession({
      targetShopId: pendingShop.id,
      disclosureReason,
      mode: 'SHOP',
      note: 'Accessed from support shops list',
      returnPath: currentPath,
    });
    navigate('/shop-floor');
  };

  const filteredShops = shops.filter((shop) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      shop.name.toLowerCase().includes(searchLower) ||
      shop.id.toLowerCase().includes(searchLower)
    );
  });

  if (!canAccessSupportMode) {
    return (
      <div className="min-h-screen bg-background">
        <TopBar />
        <div className="container mx-auto p-6 max-w-3xl">
          <Card className="p-12 text-center">
            <Building className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h1 className="text-2xl font-bold mb-2">403 — Support Mode Restricted</h1>
            <p className="text-muted-foreground">
              Support mode is only available to managers of the internal OptiBay shop.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Support Mode - Shops</h1>
            <p className="text-muted-foreground">
              Select a shop to view in support mode
            </p>
          </div>
          <Button onClick={() => navigate('/support/dashboard')}>
            <LayoutDashboard className="h-4 w-4 mr-2" />
            View Dashboard
          </Button>
        </div>

        {/* Core-100 R5 CORE-BJ / OD-49: the working path into a customer shop — the shop's own approval. The list
            below shows only shops this account is already a member of (no member RLS policy admits support). */}
        <div className="mb-6">
          <SupportShopAccessRequests />
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search shops..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading shops...</p>
          </div>
        ) : filteredShops.length === 0 ? (
          <Card className="p-12 text-center">
            <Building className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-semibold mb-2">No shops found</h3>
            <p className="text-sm text-muted-foreground">
              {search ? 'Try a different search term' : 'No shops available'}
            </p>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {isInSupportMode && (
              <div className="md:col-span-2 lg:col-span-3">
                <MemberSelector />
              </div>
            )}
            {filteredShops.map((shop) => (
              <Card key={shop.id} className="p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Building className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      <h3 className="font-semibold truncate">{shop.name}</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">{shop.tier}</Badge>
                      {shop.is_internal && <Badge>Internal</Badge>}
                      {shop.alpha_access && <Badge variant="secondary">Alpha</Badge>}
                    </div>
                  </div>
                </div>
                <Button
                  className="w-full"
                  onClick={() => handleOpenShop(shop.id)}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Open in Support Mode
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>

      <SupportAccessReasonDialog
        open={!!pendingShop}
        onOpenChange={(next) => { if (!next) setPendingShop(null); }}
        targetLabel={pendingShop?.name}
        onConfirm={handleConfirmAccess}
      />
    </div>
  );
}
